import React, { forwardRef, useRef, useEffect, useState } from 'react';
import classNames from 'classnames';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { HideReason, Button } from '@trello/nachos';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { Feature } from 'app/scripts/debug/constants';
import {
  useWorkspacesPreambleBoardHeaderButtonQuery,
  useWorkspacesPreambleDismissPromptMutation,
} from '@trello/graphql/generated';
import { WorkspacesPreamblePrompt } from './WorkspacesPreamblePrompt';
import { forNamespace } from 'app/common/lib/util/i18n';
import { isBoardPath } from 'app/gamma/src/util/url';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import styles from './WorkspacesPreambleBoardHeaderButton.less';

const format = forNamespace();
const formatPreamble = forNamespace('workspaces preamble');

interface WorkspacesPreambleBoardHeaderButtonProps {
  boardId: string;
  canAutoShow: boolean;
  onCreateTeamSuccess: (teamName?: string) => void;
  windowUrl?: string;
}

function usePrevious<T>(value: T) {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

const AddToTeamButton = forwardRef<
  HTMLAnchorElement,
  { boardId: string; onClick: () => void }
>(({ boardId, onClick }, ref) => {
  const sendAnalyticEvents = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'workspacesPreambleAddToTeamButton',
      source: 'boardScreen',
      containers: {
        board: {
          id: boardId,
        },
      },
    });
    trackUe({
      category: Noun.BOARD_VIEW,
      verb: Verb.CLICKS,
      directObj: Noun.WORKSPACES_PREAMBLE_ADD_TO_TEAM_BUTTON,
      context: { boardId },
    });
  };

  return (
    <a
      className="board-header-btn board-header-btn-without-icon"
      onClick={() => {
        sendAnalyticEvents();
        onClick();
      }}
      ref={ref}
      role="button"
    >
      <span className="board-header-btn-text">
        {formatPreamble('board-header-button')}
      </span>
    </a>
  );
});

const PromptIntroScreen: React.FunctionComponent<{
  buttonCopy: string;
  onButtonClick: () => void;
}> = ({ buttonCopy, onButtonClick }) => {
  return (
    <div className={styles.workspacesPreambleBoardHeaderButtonIntro}>
      <img
        src={require('resources/images/workspaces-preamble/workspaces-preamble-prompt-board-image.png')}
        alt=""
        role="presentation"
      />
      <h2 className={styles.workspacesPreambleBoardHeaderButtonIntroTitle}>
        {formatPreamble('prompt-heading')}
      </h2>
      <p>{formatPreamble('prompt-paragraph')}</p>
      <Button
        appearance="primary"
        className={styles.workspacesPreambleBoardHeaderButtonContinueButton}
        onClick={onButtonClick}
        shouldFitContainer
      >
        {buttonCopy}
      </Button>
    </div>
  );
};

export const WorkspacesPreambleBoardHeaderButton: React.FunctionComponent<WorkspacesPreambleBoardHeaderButtonProps> = ({
  boardId,
  canAutoShow,
  onCreateTeamSuccess,
  windowUrl,
}) => {
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [hasSucceeded, setAddToSelectedTeamSucceeded] = useState(false);

  const { data } = useWorkspacesPreambleBoardHeaderButtonQuery({
    variables: { boardId },
  });

  const [dismissPrompt] = useWorkspacesPreambleDismissPromptMutation();
  const DISMISS_MESSAGE = `WorkspacesPreamblePrompt-${boardId}`;

  /**
   * For the Add to team button to be shown, the board and user must meet the following:
   * - The board does not belong to a team
   * - The board has at least 2 confirmed members
   * - The board is not owned by an enterprise
   * - The user is a board admin
   * - The user is not a member of an enterprise team
   * - The user must be confirmed
   */
  const meId = data?.member?.id;
  const meMembership = data?.board?.memberships?.find(
    (membership) => membership.idMember === meId,
  );
  const isEnterpriseOwned = data?.board?.enterpriseOwned;
  const isBoardAdmin = meMembership?.memberType === 'admin';
  const isEnterpriseMember = !!data?.member?.enterprises?.length;
  const isTeamless = !data?.board?.idOrganization;
  const numberOfConfirmedCollaborators = data?.board?.members?.reduce(
    (count, member) => (member.confirmed ? count + 1 : count),
    0,
  );
  const hasEnoughCollaborators =
    numberOfConfirmedCollaborators && numberOfConfirmedCollaborators >= 2;
  const boardAndMemberMeetRequirements = !!(
    isBoardAdmin &&
    isTeamless &&
    hasEnoughCollaborators &&
    !isEnterpriseOwned &&
    !isEnterpriseMember &&
    data?.member?.confirmed
  );

  /**
   * Prompt should only show automatically if:
   * - The member's oneTimeMessagesDismissed does not include a dismiss message for this board
   * - The page is a board view page
   * - The board view gives permission (canAutoShow)
   */
  const isBoardView = isBoardPath(
    new URL(windowUrl || window.location.href).pathname,
  );
  const oneTimeMessagesDismissed = data?.member?.oneTimeMessagesDismissed || [
    DISMISS_MESSAGE,
  ];
  const shouldShowPromptAutomatically = !!(
    boardAndMemberMeetRequirements &&
    canAutoShow &&
    isBoardView &&
    !oneTimeMessagesDismissed.includes(DISMISS_MESSAGE)
  );

  const onHide = (hideReason: HideReason) => {
    setIsPopoverVisible(false);

    // If the user closes the popover by explicitly clicking the X button, dismiss the prompt
    if (hideReason === HideReason.CLICK_CLOSE_BUTTON) {
      dismissPrompt({
        variables: { messageId: `WorkspacesPreamblePrompt-${boardId}` },
      });
    }
  };

  const onShow = () => {
    setIsPopoverVisible(true);

    Analytics.sendScreenEvent({
      name: 'workspacesPreambleAddToTeamInlineDialog',
      containers: {
        board: {
          id: boardId,
        },
      },
    });
    trackUe({
      category: Noun.BOARD_VIEW,
      verb: Verb.VIEWS,
      directObj: Noun.WORKSPACES_PREAMBLE_ADD_TO_TEAM_PROMPT,
      context: { boardId },
    });
  };

  // Show an alert if we dismiss the popover due to another member adding it to a team
  const wasTeamless = usePrevious(isTeamless);
  useEffect(() => {
    if (wasTeamless && !isTeamless && isPopoverVisible && !hasSucceeded) {
      Alerts.show('teamless-board-added-to-team', 'info', 'board', 5000);
    }
  }, [isTeamless, isPopoverVisible, wasTeamless, hasSucceeded]);

  // If the board and user do not meet the requirements for the WorkspacesPreamble feature, show the classic Personal button
  const shouldShowAddToTeamButton =
    boardAndMemberMeetRequirements || isPopoverVisible;

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-bizteam',
        feature: Feature.WorkspacesPreamble,
      }}
    >
      {shouldShowAddToTeamButton ? (
        <WorkspacesPreamblePrompt
          autoShow={shouldShowPromptAutomatically}
          boardId={boardId}
          IntroScreen={PromptIntroScreen}
          isBoardTeamless={isTeamless}
          onCreateTeamSuccess={onCreateTeamSuccess}
          onSelectTeamSuccess={() => setAddToSelectedTeamSucceeded(true)}
          onHide={onHide}
          onShow={onShow}
          successAnalyticDirectObj={Noun.WORKSPACES_PREAMBLE_ADD_TO_TEAM_PROMPT}
          successAnalyticSource="workspacesPreambleAddToTeamInlineDialog"
          TriggerButton={AddToTeamButton}
        />
      ) : (
        <a
          className={classNames(
            'board-header-btn board-header-btn-without-icon js-add-board-to-team',
            { 'no-edit': !isBoardAdmin },
          )}
        >
          <span className="board-header-btn-text">{format('personal')}</span>
        </a>
      )}
    </ErrorBoundary>
  );
};
